import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';

import { ResourceItem } from '@ppg/core/enums';
import { NavigationStateOptions } from '@ppg/core/models';
import { ColorFamilyFocusRegion } from '@ppg/features/color-families';
import { ResourceValuePipe } from '@ppg/shared/resource';
import { UrlFormatter } from '@ppg/shared/utils';

import { ColorSpecification } from '../../models/color-specification.model';
import { FormatRgbPipe } from '../../pipes/format-rgb.pipe';

@Component({
  selector: 'ppg-color-specification',
  standalone: true,
  imports: [ResourceValuePipe, FormatRgbPipe, ButtonModule, RouterLink],
  templateUrl: './color-specification.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ColorSpecificationComponent {
  readonly #router = inject(Router);
  readonly #urlFormatter = inject(UrlFormatter);

  readonly supportMultipleFamilies = input.required<boolean>();
  readonly colorFamiliesPath = input.required<string>();
  readonly colorCollectionPath = input.required<string>();
  readonly colorSpecification = input.required<ColorSpecification>();
  readonly colorDataEnabled = input.required<boolean>();
  readonly rgbEnabled = input.required<boolean>();
  readonly lrvEnabled = input.required<boolean>();
  readonly ncsEnabled = input.required<boolean>();

  readonly colorFamilyClick = output<string>();
  readonly colorCollectionClick = output<string>();

  readonly resources = computed(() => {
    return {
      colorFamily: ResourceItem.COLOR_DETAILS_COLOR_FAMILY_HEADLINE,
      collection: ResourceItem.COLOR_DETAILS_COLLECTION_HEADLINE,
      colorData: ResourceItem.COLOR_DETAILS_COLOR_DATA_HEADLINE,
    };
  });

  readonly colorFamilies = computed(() =>
    this.colorSpecification().colorFamilies.map((family) => {
      const link = {
        path: this.#getColorFamilyPath(family.name, family.typeLabel),
        fragment: `${ColorFamilyFocusRegion.familyDescription}`,
      };

      return { ...family, link };
    }),
  );

  formatPath(path: string) {
    return this.#urlFormatter.formatStringValueToUrlPath(path);
  }

  onColorCollectionClick(collectionName: string) {
    this.colorCollectionClick.emit(collectionName);
  }

  onColorFamilyClick(familyName: string, navigationPath: string, fragment: string) {
    this.colorFamilyClick.emit(familyName);
    this.#navigateToPath(navigationPath, fragment);
  }

  #navigateToPath(navigationPath: string, fragment: string) {
    const state: NavigationStateOptions = { shouldScroll: true, fragment };

    this.#router.navigate([navigationPath], {
      fragment: fragment,
      skipLocationChange: false,
      state,
    });
  }

  #getColorFamilyPath(familyName: string, familyType: string) {
    const familyTypeSegment = familyType && this.supportMultipleFamilies() ? `/${familyType}` : '';
    const familyNameSegment = `/${familyName}`;

    const relativePath = `${this.colorFamiliesPath()}${familyTypeSegment}${familyNameSegment}`;

    return this.formatPath(relativePath);
  }
}
